import {
  Button,
  Input,
  Paginate,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import React, { useState, useEffect, useCallback } from 'react';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import './styles.scss';
import { useNavigate } from 'react-router-dom';

import ArchiveModal from 'components/modals/archive-manage-meals-modal';
import BulkActions from 'pages/main-store/pricing/configuration/bulk-vendor-actions';
import { ConversionListFormType, ConversionListType } from 'models/pricing.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { debounce, sortConvertionItem, handleApiError } from 'utils/functions';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import usePricingContext from 'contexts/pricing.context';
import AddVendorModal from 'pages/main-store/pricing/configuration/add-configuration-modal';
import useRoleContext from 'contexts/role.context';
import { InventoryModule } from 'models/permission.model';
import { EDIT_PERMISSION_ACTIONS } from 'utils/authorization';

const PrepConversionList = () => {
  const navigate = useNavigate();
  const { can } = useRoleContext();
  const { setStep, setConversionListFormData, setSelectedConversionItem } = usePricingContext();
  const [selectedItems, setSelectedItems] = useState<ConversionListType[]>([]);
  const [openedItems, setOpenedItems] = useState<string[]>([]);
  const [singleSelectedVendor, setSingleSelectedVendor] = useState<ConversionListType | null>(null);
  const [vendorsArray, setVendorsArray] = useState<ConversionListType[]>([]);
  const [showAddVendorModal, setShowAddvendorModal] = useState(false);
  const [isFetchingMeals, setIsFetchingMeals] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showBulkActions, setShowBulkActions] = useState<boolean>(false);

  const toggleAddVendor = () => {
    setShowAddvendorModal(!showAddVendorModal);
  };

  const isAllSelected = () => {
    return vendorsArray.length > 0 && vendorsArray.length === selectedItems.length;
  };
  const selectVendor = (id: string) => {
    const vendor = vendorsArray.find((vendor) => vendor.ingredient_id === id);
    if (vendor) {
      if (selectedItems.includes(vendor)) {
        setSelectedItems(selectedItems.filter((vendor) => vendor.ingredient_id !== id));
      } else {
        setSelectedItems([...selectedItems, vendor]);
      }
    }
  };

  const toggleItemRow = (id: string) => {
    if (openedItems.includes(id)) {
      setOpenedItems(openedItems?.filter((_) => _ !== id));
    } else {
      setOpenedItems([...openedItems, id]);
    }
  };

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const canEditItemConversion = can(InventoryModule.ItemConversion, EDIT_PERMISSION_ACTIONS);

  const toggleAll = () => {
    if (!canEditItemConversion) return;
    if (isAllSelected()) {
      setSelectedItems([]);
    } else {
      setSelectedItems(vendorsArray);
    }
  };

  const handleArchive = () => {
    if (!canEditItemConversion) return;
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async () => {
    if (!canEditItemConversion) return;
    setIsArchiving(true);
    try {
      const itemsToArchive =
        selectedItems.length > 0
          ? selectedItems
          : singleSelectedVendor
          ? [singleSelectedVendor]
          : [];

      if (itemsToArchive.length === 0) return;

      const archivePromises = itemsToArchive.map((item) =>
        apiService.put(`ingredient-mix/${item.id}/archive`, {
          version: 'v2'
        })
      );

      await Promise.all(archivePromises);

      setSingleSelectedVendor(null);
      setSelectedItems([]);
      setShowArchiveModal(false);

      await fetchMeals(search, '', currentPage);
      toast(<ToastMessage message='Meal(s) archived successfully' type='success' />);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleEdit = (item?: ConversionListType) => {
    if (!canEditItemConversion) return;
    const items: ConversionListFormType[] = item
      ? [sortConvertionItem(item)]
      : selectedItems?.map((_) => sortConvertionItem(_));
    const itemsSelected = {
      ...items[0],
      itemIndex: 0
    };
    navigate(`${MAIN_STORE_ROUTE_KEYS.EDIT_MEALS_CONFIGURATION}`, {
      state: items
    });
    setConversionListFormData(items);
    setSelectedConversionItem(itemsSelected);
    setStep(2);
  };

  const fetchMeals = async (searchString: string, categoryString: string, page: number) => {
    try {
      setIsFetchingMeals(true);
      const response = await apiService.get(`ingredient-mix`, {
        version: 'v2',
        params: {
          ...(searchString ? { name: searchString } : {}),
          page,
          type: 'prep'
        }
      });
      if (response) {
        setVendorsArray(response?.data?.data || []);
        setLastPage(Math.ceil(response?.data?.pagination.total / 10));
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching meals'}
          type='error'
        />
      );
    } finally {
      setIsFetchingMeals(false);
    }
  };

  const debounceFetchVendors = useCallback(debounce(fetchMeals, 1000), []);

  useEffect(() => {
    setIsFetchingMeals(true);
    clearVendorsList();
    debounceFetchVendors(search, '', currentPage);
  }, [search, currentPage]);

  useEffect(() => {
    if (selectedItems.length > 1) {
      setShowBulkActions(true);
    } else {
      setShowBulkActions(false);
    }
  }, [selectedItems]);

  const clearVendorsList = () => {
    setVendorsArray([]);
    setSelectedItems([]);
  };

  return (
    <div className='px-2 md:px-6 mt-2 mb-10 vendors'>
      <div className='flex justify-between items-center mb-8'>
        <div>
          <h4 className='text-[#2E3031] text-2xl font-semibold'>Prep Team Conversion list</h4>
          <p className='text-[#6F7376] text-sm'>
            This is the ingredient breakdown for meal items that need to be prepped
          </p>
        </div>
        {canEditItemConversion && (
          <Button
            onClick={toggleAddVendor}
            variant='block'
            color='primary'
            className='w-[167px] text-white'
            small>
            <AddIcon className='w-[18px] h-[18px] text-white' />
            Add Meal
          </Button>
        )}
      </div>
      <div className='flex justify-between items-center mb-8'>
        <div className='flex gap-3 items-center'>
          <Input
            type='search'
            placeholder='Search meals'
            className='w-[300px] search-input'
            inputClass='h-[36px]'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => {
            clearVendorsList();
            setCurrentPage(e.selected + 1);
          }}
        />
      </div>
      <Table
        headers={[]}
        isAllSelected={isAllSelected()}
        onToggleAll={toggleAll}
        loading={isFetchingMeals}>
        {vendorsArray.map((vendor) => {
          const isOpened = openedItems?.includes(vendor.ingredient?.id);

          return (
            <>
              <TableRow
                key={vendor.id}
                className={`table_row transition-colors ease-in-out duration-500  inactive_row ${
                  isOpened ? 'bg-[#F4F5F5]' : 'bg-white'
                }`}>
                <TableData className='w-0'>
                  <div
                    onClick={() => toggleItemRow(vendor.ingredient.id)}
                    className='flex justify-start items-center gap-3 cursor-pointer w-full'>
                    <Input
                      onClick={(e) => e.stopPropagation()}
                      type='checkbox'
                      className='mr-2'
                      checkColor='red'
                      checked={selectedItems.includes(vendor)}
                      disabled={!canEditItemConversion}
                      onChange={() => {
                        if (!canEditItemConversion) return;
                        selectVendor(vendor.ingredient.id);
                      }}
                    />
                    <span className='text-base font-semibold'>{vendor.ingredient.name}</span>
                    <span className='text-sm text-gray-12 bg-gray-13 px-2 py-0.5 rounded-full'>
                      {vendor.no_of_portions} {vendor.portion_type}
                    </span>
                  </div>
                </TableData>

                <TableData className='w-0'>
                  <div
                    className='opacity-0 cursor-pointer'
                    onClick={() => toggleItemRow(vendor.ingredient.id)}>
                    Quantity
                  </div>
                </TableData>
                <TableData className='w-0'>
                  {canEditItemConversion ? (
                    <div className='flex gap-3 w-[52px]'>
                      <button
                        onClick={() => handleEdit(vendor)}
                        className='flex justify-center items-center gap-1 bg-white px-3 py-1 rounded-full'>
                        <EditIcon className='w-[18px] h-[18px]' />
                        <span className='text-gray-12 font-medium'>Edit</span>
                      </button>

                      <button
                        onClick={() => {
                          setSingleSelectedVendor(vendor);
                          setShowArchiveModal(true);
                        }}
                        className='flex justify-center items-center gap-1 bg-white px-3 py-1 rounded-full'>
                        <ArchiveIcon className='w-[18px] h-[18px]' />
                        <span className='text-[#E60A2B] font-medium'>Archive</span>
                      </button>
                    </div>
                  ) : (
                    ''
                  )}
                </TableData>
              </TableRow>
              {openedItems?.includes?.(vendor.ingredient.id) ? (
                <>
                  <TableRow key={vendor.id} className='table_row'>
                    <TableData className=''>
                      <span>Ingredients</span>
                    </TableData>

                    <TableData className=''>
                      <span>Quantity</span>
                    </TableData>
                    <TableData>
                      <span></span>
                    </TableData>
                  </TableRow>

                  {vendor?.items?.map((item) => (
                    <TableRow key={item.id} className='table_row '>
                      <TableData className='py-0'>
                        <span>{item.ingredient.name}</span>
                      </TableData>

                      <TableData className='py-0'>
                        <span>
                          {item.quantity} {item.ingredient.unit_type}
                        </span>
                      </TableData>
                      <TableData>
                        <span></span>
                      </TableData>
                    </TableRow>
                  ))}
                </>
              ) : null}

              <tr className='h-11' />
            </>
          );
        })}
      </Table>
      {showArchiveModal && canEditItemConversion && (
        <ArchiveModal
          show
          onClose={() => setShowArchiveModal(false)}
          singleSelectedVendor={singleSelectedVendor}
          selectedVendor={selectedItems}
          onConfirm={handleArchiveConfirm}
          loading={isArchiving}
        />
      )}
      {showAddVendorModal && <AddVendorModal show={showAddVendorModal} onClose={toggleAddVendor} />}
      {canEditItemConversion && selectedItems.length !== 0 && (
        <BulkActions
          item={selectedItems}
          deleteFunc={handleArchive}
          showBulkActions={showBulkActions}
          setShowBulkActions={setShowBulkActions}
          setSelectedItems={setSelectedItems}
        />
      )}
    </div>
  );
};

export default PrepConversionList;
